import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import { useSearchParams } from "react-router-dom";
import { APP_NAME } from "../helpers/constants";

function apiBase() {
  const env = String(process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
  if (env) return env;
  if (typeof window !== "undefined") {
    return `${window.location.origin}/api`;
  }
  return "http://localhost:4000/api";
}

/** Never append APP_SECRET to the request URL; only `X-App-Secret` (see .env.example). */
function ceoPulseDashboardUrl() {
  return `${apiBase()}/ceo-pulse/dashboard`;
}

/** Remove legacy `?secret=` from the SPA URL so it is not kept in history or leaked via Referer. */
function stripSecretFromBrowserUrl() {
  if (typeof window === "undefined") return;
  const u = new URL(window.location.href);
  if (!u.searchParams.has("secret")) return;
  u.searchParams.delete("secret");
  const qs = u.searchParams.toString();
  window.history.replaceState({}, "", `${u.pathname}${qs ? `?${qs}` : ""}${u.hash}`);
}

const TIER_ORDER = ["free", "starter", "growth", "scale", "enterprise"];

/**
 * Secret-gated executive snapshot. Paste the same value as server `APP_SECRET`;
 * it is sent only in the `X-App-Secret` header (never in the API URL). If you still
 * have an old bookmark with `?secret=`, it is used once then stripped from the address bar.
 */
export default function CeoPulsePage() {
  const [searchParams] = useSearchParams();
  const secretFromUrl = useMemo(() => {
    const s = searchParams.get("secret");
    return typeof s === "string" ? s : "";
  }, [searchParams]);

  const [secretInput, setSecretInput] = useState(secretFromUrl);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (secretFromUrl) setSecretInput(secretFromUrl);
  }, [secretFromUrl]);

  const run = useCallback(async (secretRaw) => {
    const trimmed = String(secretRaw || "").trim();
    setError("");
    setData(null);
    if (!trimmed) {
      setError(
        "Paste APP_SECRET (same value as on the API server). It is sent only in the X-App-Secret header, not in the request URL."
      );
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(ceoPulseDashboardUrl(), {
        method: "GET",
        headers: { "X-App-Secret": trimmed },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.error || res.statusText || "Request failed");
      }
      setData(body);
      stripSecretFromBrowserUrl();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (secretFromUrl.trim()) {
      run(secretFromUrl);
    }
  }, [secretFromUrl, run]);

  const publicPageUrl =
    typeof window !== "undefined" ? `${window.location.origin}/ceo-pulse` : "/ceo-pulse";

  return (
    <Page>
      <Helmet>
        <title>CEO Pulse · {APP_NAME}</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <Panel>
        <Title>CEO pulse</Title>
        <Lead>
          Aggregated health and growth metrics (no emails or addresses). Treat{" "}
          <code>APP_SECRET</code> like a password: use a long random value, never share links
          that embed it, and do not rely on it living in the browser address bar (history,
          referrers, and server logs are real risks). This page sends the secret only in the{" "}
          <code>X-App-Secret</code> header, not in the API request URL.
        </Lead>
        <UrlBox>
          <Muted>Page URL (safe to bookmark; no secret)</Muted>
          <Mono>{publicPageUrl}</Mono>
          <Warn>
            Generate a strong secret on the server, e.g.{" "}
            <code>openssl rand -hex 32</code>. If you previously bookmarked{" "}
            <code>?secret=…</code>, open that link once; after a successful load the secret is
            removed from the URL automatically.
          </Warn>
        </UrlBox>
        <Label htmlFor="ceo-secret">APP_SECRET</Label>
        <SecretInput
          id="ceo-secret"
          type="password"
          autoComplete="off"
          placeholder="Paste APP_SECRET"
          value={secretInput}
          onChange={(e) => setSecretInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") run(secretInput);
          }}
        />
        <Actions>
          <RunButton
            type="button"
            onClick={() => run(secretInput.trim() || secretFromUrl.trim())}
            disabled={loading}
          >
            {loading ? "Loading…" : "Refresh"}
          </RunButton>
        </Actions>
        {error ? <Err role="alert">{error}</Err> : null}

        {data && data.ok ? (
          <Grid>
            <Card>
              <CardTitle>Environment</CardTitle>
              <Muted>
                NODE_ENV: <strong>{data.environment?.nodeEnv ?? "N/A"}</strong>
                {data.environment?.firestorePingMs != null ? (
                  <>
                    {" "}
                    · Firestore ping: <strong>{data.environment.firestorePingMs} ms</strong>
                  </>
                ) : null}
              </Muted>
              {data.environment?.firestorePingError ? (
                <Err>{data.environment.firestorePingError}</Err>
              ) : null}
              <CheckList>
                {(data.environment?.checks || []).map((row) => (
                  <CheckRow key={row.key}>
                    <Dot $ok={row.ok} />
                    <span>{row.label}</span>
                    <Badge $ok={row.ok}>{row.ok ? "OK" : "Missing"}</Badge>
                  </CheckRow>
                ))}
              </CheckList>
            </Card>

            <Card>
              <CardTitle>Landlords by tier</CardTitle>
              <Muted>
                Users scanned: <strong>{data.users?.totalScanned?.toLocaleString?.() ?? data.users?.totalScanned}</strong>
                {data.users?.scanIncomplete ? (
                  <span> (scan capped; increase backend limits if needed)</span>
                ) : null}
              </Muted>
              <TierTable>
                <thead>
                  <tr>
                    <th>Tier</th>
                    <th align="right">Count</th>
                  </tr>
                </thead>
                <tbody>
                  {TIER_ORDER.map((tier) => (
                    <tr key={tier}>
                      <td>{tier}</td>
                      <td align="right">
                        {data.users?.tierCounts?.[tier]?.toLocaleString?.() ??
                          data.users?.tierCounts?.[tier] ??
                          0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </TierTable>
              <Muted style={{ marginTop: "0.75rem" }}>
                Stripe customer IDs on profile:{" "}
                <strong>{data.users?.withStripeCustomerId?.toLocaleString?.() ?? 0}</strong>
                · Free-gateway signup flag (legacy field):{" "}
                <strong>{data.users?.freeGatewaySignupApprox?.toLocaleString?.() ?? 0}</strong>
              </Muted>
            </Card>

            <Card>
              <CardTitle>Signup velocity</CardTitle>
              <Muted>Based on parseable profile <code>createdAt</code> (Firestore or ISO).</Muted>
              <SubTable>
                <thead>
                  <tr>
                    <th />
                    <th align="right">24h</th>
                    <th align="right">7d</th>
                    <th align="right">28d</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>All profiles</td>
                    <td align="right">{data.signupVelocity?.allProfiles?.last24h ?? 0}</td>
                    <td align="right">{data.signupVelocity?.allProfiles?.last7d ?? 0}</td>
                    <td align="right">{data.signupVelocity?.allProfiles?.last28d ?? 0}</td>
                  </tr>
                  <tr>
                    <td>Free tier (effective)</td>
                    <td align="right">{data.signupVelocity?.freeTier?.last24h ?? 0}</td>
                    <td align="right">{data.signupVelocity?.freeTier?.last7d ?? 0}</td>
                    <td align="right">{data.signupVelocity?.freeTier?.last28d ?? 0}</td>
                  </tr>
                </tbody>
              </SubTable>
              <Muted>
                Profiles missing <code>createdAt</code>:{" "}
                <strong>{data.signupVelocity?.profilesWithMissingCreatedAt ?? 0}</strong>
              </Muted>
            </Card>

            <Card>
              <CardTitle>Subscription status (raw)</CardTitle>
              <Muted>Counts from Firestore <code>subscriptionStatus</code> field.</Muted>
              <SubTable>
                <tbody>
                  {Object.entries(data.users?.subscriptionStatusBreakdown || {})
                    .sort((a, b) => b[1] - a[1])
                    .map(([k, v]) => (
                      <tr key={k}>
                        <td>{k || "N/A"}</td>
                        <td align="right">{v}</td>
                      </tr>
                    ))}
                </tbody>
              </SubTable>
            </Card>

            <Card $wide>
              <CardTitle>Pipeline</CardTitle>
              <PipelineGrid>
                <Stat>
                  <StatLabel>Vendor applicants (total)</StatLabel>
                  <StatValue>{fmt(data.pipeline?.vendorLeadsTotal)}</StatValue>
                </Stat>
                <Stat>
                  <StatLabel>Vendor applicants (24h / 7d / 28d)</StatLabel>
                  <StatValue>
                    {fmt(data.pipeline?.vendorLeadsLast24h)} / {fmt(data.pipeline?.vendorLeadsLast7d)} /{" "}
                    {fmt(data.pipeline?.vendorLeadsLast28d)}
                  </StatValue>
                </Stat>
                <Stat>
                  <StatLabel>Maintenance tickets</StatLabel>
                  <StatValue>{fmt(data.pipeline?.jobRequestsTotal)}</StatValue>
                </Stat>
                <Stat>
                  <StatLabel>Liaison dispatches</StatLabel>
                  <StatValue>{fmt(data.pipeline?.liaisonDispatchesTotal)}</StatValue>
                </Stat>
              </PipelineGrid>
              {data.pipeline?.vendorLeadsError ? (
                <Err>Vendor applicant aggregates: {data.pipeline.vendorLeadsError}</Err>
              ) : null}
              {data.pipeline?.jobRequestsError ? (
                <Err>Job Requests: {data.pipeline.jobRequestsError}</Err>
              ) : null}
              {data.pipeline?.liaisonDispatchesError ? (
                <Err>Liaison: {data.pipeline.liaisonDispatchesError}</Err>
              ) : null}
            </Card>

            <Card $wide>
              <CardTitle>Raw JSON</CardTitle>
              <Muted>Generated {data.generatedAt}</Muted>
              <Pre>{JSON.stringify(data, null, 2)}</Pre>
            </Card>
          </Grid>
        ) : null}
      </Panel>
    </Page>
  );
}

function fmt(n) {
  if (n == null) return "N/A";
  return typeof n === "number" ? n.toLocaleString() : String(n);
}

const Page = styled.main`
  min-height: calc(100vh - 2rem);
  padding: 2rem 1rem 3rem;
  background: #0a0a0a;
  color: #fafafa;
  font-family: ui-sans-serif, system-ui, sans-serif;
`;

const Panel = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: 1.25rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  font-weight: 800;
  margin: 0 0 1rem;
`;

const Lead = styled.p`
  font-size: 0.9rem;
  line-height: 1.55;
  color: #a3a3a3;
  max-width: 46rem;
  margin: 0 0 1rem;

  code {
    font-size: 0.85em;
    color: #e5e5e5;
  }
`;

const UrlBox = styled.div`
  border: 1px solid #262626;
  padding: 0.75rem 1rem;
  margin-bottom: 1.25rem;
  background: #111;
`;

const Mono = styled.div`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.75rem;
  word-break: break-all;
  color: #d4d4d4;
  margin-top: 0.35rem;
`;

const Warn = styled.p`
  margin: 0.75rem 0 0;
  font-size: 0.78rem;
  line-height: 1.5;
  color: #fcd34d;

  code {
    font-size: 0.85em;
    color: #fde68a;
  }
`;

const Muted = styled.p`
  margin: 0 0 0.5rem;
  font-size: 0.8rem;
  color: #737373;

  code {
    color: #a3a3a3;
  }

  strong {
    color: #e5e5e5;
    font-weight: 600;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 0.7rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #737373;
  margin-bottom: 0.35rem;
`;

const SecretInput = styled.input`
  width: 100%;
  max-width: 28rem;
  padding: 0.6rem 0.75rem;
  border: 1px solid #404040;
  border-radius: 0;
  background: #171717;
  color: #fafafa;
  font-size: 0.95rem;
`;

const Actions = styled.div`
  margin: 0.75rem 0 1rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
`;

const RunButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid #fafafa;
  background: #fafafa;
  color: #0a0a0a;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  font-size: 0.75rem;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const Err = styled.div`
  color: #fca5a5;
  font-size: 0.85rem;
  margin: 0.5rem 0 1rem;
`;

const Grid = styled.div`
  display: grid;
  gap: 1rem;
  margin-top: 1.5rem;
  grid-template-columns: 1fr;

  @media (min-width: 900px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Card = styled.section`
  border: 1px solid #262626;
  padding: 1rem 1.1rem;
  background: #111;
  grid-column: ${(p) => (p.$wide ? "1 / -1" : "auto")};
`;

const CardTitle = styled.h2`
  margin: 0 0 0.5rem;
  font-size: 0.85rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 700;
  color: #e5e5e5;
`;

const CheckList = styled.ul`
  list-style: none;
  margin: 0.75rem 0 0;
  padding: 0;
  max-height: 320px;
  overflow: auto;
`;

const CheckRow = styled.li`
  display: grid;
  grid-template-columns: 10px 1fr auto;
  gap: 0.5rem 0.65rem;
  align-items: center;
  font-size: 0.8rem;
  padding: 0.25rem 0;
  border-bottom: 1px solid #1f1f1f;
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => (p.$ok ? "#4ade80" : "#f87171")};
`;

const Badge = styled.span`
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  padding: 0.15rem 0.4rem;
  border: 1px solid ${(p) => (p.$ok ? "#166534" : "#7f1d1d")};
  color: ${(p) => (p.$ok ? "#bbf7d0" : "#fecaca")};
`;

const TierTable = styled.table`
  width: 100%;
  margin-top: 0.75rem;
  border-collapse: collapse;
  font-size: 0.85rem;

  th,
  td {
    padding: 0.35rem 0.25rem;
    border-bottom: 1px solid #262626;
  }

  th {
    text-align: left;
    color: #737373;
    font-weight: 600;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
`;

const SubTable = styled.table`
  width: 100%;
  margin-top: 0.65rem;
  border-collapse: collapse;
  font-size: 0.85rem;

  th,
  td {
    padding: 0.35rem 0.25rem;
    border-bottom: 1px solid #262626;
  }

  th {
    color: #737373;
    font-weight: 600;
    font-size: 0.7rem;
    text-transform: uppercase;
  }
`;

const PipelineGrid = styled.div`
  display: grid;
  gap: 1rem;
  margin-top: 0.75rem;

  @media (min-width: 700px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Stat = styled.div`
  border: 1px solid #262626;
  padding: 0.65rem 0.75rem;
`;

const StatLabel = styled.div`
  font-size: 0.65rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #737373;
`;

const StatValue = styled.div`
  font-size: 1.1rem;
  font-weight: 700;
  margin-top: 0.25rem;
`;

const Pre = styled.pre`
  margin: 0.75rem 0 0;
  padding: 0.75rem;
  background: #0a0a0a;
  border: 1px solid #262626;
  font-size: 0.68rem;
  overflow: auto;
  max-height: 420px;
  color: #d4d4d4;
`;
